import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getManifest, getStyles } from '../lib/getVoices';
import { newVoices } from '../lib/newVoices';
import type { Gender, StyleText, Voice, VoiceDemoDataType } from '../lib/types';

const getFormattedVoiceData = (voices: Voice[]): VoiceDemoDataType[] => {
  const grouped = voices.reduce(
    (acc, voice) => {
      voice.models.forEach((model) => {
        model.languages.forEach((lang) => {
          const locale = lang.locale;
          if (!acc[locale]) {
            acc[locale] = [];
          }
          if (!acc[locale].find((v) => v.id === voice.id)) {
            acc[locale].push(voice);
          }
        });
      });
      return acc;
    },
    {} as Record<string, Voice[]>,
  );

  const languageNames =
    // eslint-disable-next-line compat/compat
    typeof Intl.DisplayNames !== 'undefined'
      ? // eslint-disable-next-line compat/compat
        new Intl.DisplayNames(['en'], { type: 'language' })
      : { of: (code: string) => code }; // Fallback to return the code itself
  const formattedVoice: VoiceDemoDataType[] = [];

  for (const languageCode in grouped) {
    const voices = grouped[languageCode];
    const languageName =
      languageNames.of(languageCode.split('-')[0]) ?? 'en-us';
    formattedVoice.push({
      languageCode,
      languageName,
      voices: voices.map((voice: Voice) => {
        return {
          id: voice.id,
          region: languageCode.split('-')[1]?.toLocaleUpperCase(),
          displayName: voice.display_name,
          name: voice.id,
          gender: voice.gender as Gender,
          engine: voice.type,
          languageCode: languageCode,
          image: voice.avatar_image ?? '',
          imageLarge: voice.avatar_image ?? '',
          customText: '',
          previewAudio: voice.preview_audio!,
          description: '',
          isCelebrity: false,
          models: voice.models.map((model) => model.name),
        };
      }),
    });
  }

  return formattedVoice;
};

interface VoicesContextValue {
  voices: VoiceDemoDataType[];
  refetch: () => Promise<void>;
  error: string | undefined;
  loading: boolean;
  styleText: StyleText;
  staticAudioFilesManifest: string[];
}

const VoicesContext = createContext<VoicesContextValue>({
  voices: [],
  refetch: () => Promise.resolve(),
  error: undefined,
  loading: false,
  styleText: {},
  staticAudioFilesManifest: [],
});

export function VoicesProvider({ children }: PropsWithChildren) {
  const [voices, setVoices] = useState<VoiceDemoDataType[]>([]);
  const [styleText, setStyleText] = useState<StyleText>({});
  const [staticAudioFilesManifest, setStaticAudioFilesManifest] = useState<
    string[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchVoices = useCallback(async () => {
    setLoading(true);
    try {
      const currVoices = newVoices;
      const formattedVoices = getFormattedVoiceData(currVoices);
      setVoices(formattedVoices);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
      console.error(err);
      setVoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStyle = useCallback(async () => {
    try {
      const styleText = await getStyles();
      setStyleText(styleText);
    } catch (err) {
      console.error(err);
      return {};
    }
  }, []);


  const fetchStaticAudioFilesManifest = useCallback(async () => {
    try {
      const manifest = await getManifest();
      setStaticAudioFilesManifest(manifest);
    } catch (err) {
      console.error(err);
      setStaticAudioFilesManifest([]);
    }
  }, []);


  useEffect(() => {
    void fetchStyle();
    void fetchStaticAudioFilesManifest();

  }, [fetchStyle, fetchStaticAudioFilesManifest]);

  useEffect(() => {
    fetchVoices().catch((err: Error) => {
      throw new Error(err.message);
    });
  }, [fetchVoices]);

  const value = useMemo<VoicesContextValue>(
    () => ({
      voices,
      refetch: fetchVoices,
      error,
      loading,
      styleText,
      staticAudioFilesManifest,
    }),
    [
      voices,
      fetchVoices,
      error,
      loading,
      styleText,
      staticAudioFilesManifest,
    ],
  );

  return (
    <VoicesContext.Provider value={value}>{children}</VoicesContext.Provider>
  );
}

export const useVoices = () => useContext(VoicesContext);
